import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import clamp from 'lodash/clamp.js';
import get from 'lodash/get.js';
import isFunction from 'lodash/isFunction.js';
import isNil from 'lodash/isNil.js';
import { filterGroups, filterOptions, flattenOptions, not, createTaggableOption, isObject, isEmpty, flatAndStrip, calculateMinMenuWidthFromLabels, getNextWrappingIndex, sortData } from './multiselect-helper.js';
import PendoMultiselectProps from './pendo-multiselect-props.js';
import { getBorderWidth } from '../../utils/dom.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _empty() {}
function _invokeIgnored(body) {
  var result = body();
  if (result && result.then) {
    return result.then(_empty);
  }
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
var PendoMultiselectCore = {
  name: 'PendoMultiselectCore',
  mixins: [PendoMultiselectProps],
  render() {
    return this.$scopedSlots.default(this);
  },
  provide() {
    return {
      multiselect: this
    };
  },
  inject: {
    $form: {
      default: ''
    },
    $formItem: {
      default: ''
    }
  },
  data() {
    return {
      computedMenuWidth: this.minMenuWidth,
      inputValue: '',
      isFocused: false,
      isOpen: false,
      pointer: -1,
      selectedValuePointer: -1,
      shouldScrollToFocusedOption: false,
      triggerWidth: 0
    };
  },
  computed: {
    computedPlaceholder() {
      if (this.multiple) {
        return this.placeholder;
      }
      return this.getOptionLabel(this.selectedValue) || this.placeholder;
    },
    filteredOptions() {
      const search = this.inputValue || '';
      const normalizedSearch = search.toLowerCase().trim();
      let options = this.options.concat();
      if (this.internalSearch) {
        options = this.hasGroups ? filterGroups(normalizedSearch, this.labelKey, this.groupOptionsKey, this.groupLabelKey, this.formatOptionLabel)(options) : filterOptions(options, normalizedSearch, this.labelKey, this.formatOptionLabel);
      } else {
        options = this.hasGroups ? flattenOptions(this.groupOptionsKey, this.groupLabelKey, this.valueKey, this.groupSelect)(options) : options;
      }
      if (this.hideSelectedOptions) {
        if (this.hasGroups) {
          options = options.map(group => {
            if (!group[this.groupOptionsKey] || !group[this.groupOptionsKey].length) {
              return [];
            }
            const groupOptions = group[this.groupOptionsKey].filter(not(this.isOptionSelected));
            return groupOptions.length ? _objectSpread2(_objectSpread2({}, group), {}, {
              [this.groupLabelKey]: group[this.groupLabelKey],
              [this.groupOptionsKey]: groupOptions
            }) : [];
          });
        } else {
          options = options.filter(not(this.isOptionSelected));
        }
      }
      if (this.taggable && search.length && this.isNewOption(search)) {
        const option = createTaggableOption({
          search,
          hasGroups: this.hasGroups,
          groupOptionsKey: this.groupOptionsKey,
          groupLabelKey: this.groupLabelKey,
          valueKey: this.valueKey
        });
        if (this.tagPosition === 'bottom') {
          options.push(option);
        } else {
          options.unshift(option);
        }
      }
      if (this.sortBy || this.sortMethod) {
        let sortFunction;
        if (this.sortMethod) {
          sortFunction = this.sortMethod;
        }
        if (this.sortBy) {
          sortFunction = (a, b) => sortData(a, b, this);
        }
        options = [...options].sort(sortFunction);
        if (this.hasGroups) {
          options.forEach(group => {
            if (!group[this.groupOptionsKey] || !group[this.groupOptionsKey].length) {
              return;
            }
            group[this.groupOptionsKey] = [...group[this.groupOptionsKey]].sort(sortFunction);
          });
        }
      }
      return options;
    },
    flattenedOptions() {
      return flattenOptions(this.groupOptionsKey, this.groupLabelKey, this.valueKey, this.groupSelect)(this.filteredOptions);
    },
    hasGroups() {
      return this.options.some(option => isObject(option) && option[this.groupOptionsKey]);
    },
    isDisabled() {
      const isComponentOrFormDisabled = this.disabled || get(this, '$form.disabled', false);
      if (this.disableWhileLoading) {
        return this.loading || isComponentOrFormDisabled;
      }
      return isComponentOrFormDisabled;
    },
    isFullWidth() {
      return this.minTriggerWidth === '100%' || this.fullWidth;
    },
    isPlaceholderVisible() {
      if (this.model.length && !isEmpty(this.selectedValue)) {
        return false;
      }
      return !this.isOpen || !this.isSearchable;
    },
    isSearchable() {
      if (this.searchable === false) {
        return false;
      }
      if (this.searchable || this.taggable) {
        return true;
      }
      if (this.searchThreshold) {
        return this.optionCount > this.searchThreshold;
      }
    },
    isSearchIconVisible() {
      if (this.taggable || this.multiple) {
        return !this.showSelectedValues;
      }
      if (this.isSearchable && this.isOpen) {
        return true;
      }
      return false;
    },
    isSelectedLabelVisible() {
      return !!(!isEmpty(this.selectedValue) && !this.visibleSelectedValues.length && (!this.isOpen || !this.isSearchable));
    },
    allOptionsSelected() {
      if (!this.model.length) {
        return false;
      }
      let options = this.options.concat();
      if (this.hasGroups) {
        options = flattenOptions(this.groupOptionsKey, this.groupLabelKey, this.valueKey, this.groupSelect)(options).filter(option => option.type !== 'group');
      }
      return options.filter(option => !this.isOptionSelected(option)).length === 0;
    },
    model() {
      if (this.value || this.value === 0) {
        return Array.isArray(this.value) ? this.value : [this.value];
      }
      return [];
    },
    optionCount() {
      const {
        options
      } = this;
      let count = 0;
      options.forEach(groupOrOption => {
        if (groupOrOption[this.groupOptionsKey]) {
          count++;
          groupOrOption[this.groupOptionsKey].forEach(() => count++);
        } else {
          count++;
        }
      });
      return count;
    },
    optionKeys() {
      let {
        options
      } = this;
      if (this.hasGroups) {
        options = flatAndStrip(this.groupOptionsKey, this.groupLabelKey, this.valueKey, options);
      }
      return options.map(option => this.formatOptionLabel(option, this.labelKey).toString());
    },
    selectedLabel() {
      if (!this.showSelectedValues) {
        return this.placeholder;
      }
      if (this.multiple) {
        return this.isSearchable ? '' : this.placeholder;
      }
      if (this.model.length) {
        return this.getOptionLabel(this.model[0]);
      }
      return this.isSearchable ? '' : this.placeholder;
    },
    selectedValue() {
      if (this.multiple) {
        return this.model;
      }
      if (this.model.length === 0) {
        return null;
      }
      return this.model[0];
    },
    shouldAnimateSearchEnterExit() {
      if (!this.showSelectedValues && (this.taggable || this.multiple)) {
        return this.isSearchable;
      }
      return this.isSearchable && !this.taggable && !this.multiple;
    },
    showSelectedValueTags() {
      return this.showSelectedValues && (this.taggable || this.multiple);
    },
    visibleSelectedValues() {
      if (this.multiple && this.showSelectedValues) {
        return this.model.slice(0, this.limit);
      }
      return [];
    }
  },
  watch: {
    pointer() {
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      this.$emit('highlighted-option-change', options[this.pointer]);
    },
    selectedValuePointer() {
      this.$emit('selected-value-pointer-change', this.visibleSelectedValues[this.selectedValuePointer]);
    },
    filteredOptions() {
      this.pointerAdjust();
    },
    options() {
      this.calculateMenuWidthFromOptionLabels();
      if (this.preselectFirst) {
        this.preselect();
      }
    },
    inputValue() {
      this.$emit('input-value-change', this.inputValue, this.id);
      this.schedulePopperUpdate();
    },
    model() {
      if (this.stateless && this.model.length) {
        this.inputValue = '';
        this.$emit('input', this.multiple ? [] : null);
      }
      this.$emit('all-selected-change', this.allOptionsSelected, this.id);
      this.schedulePopperUpdate();
    },
    loading() {
      if (!this.loading && this.preselectFirst) {
        this.preselect();
      }
    }
  },
  mounted() {
    this.calculateMenuWidthFromOptionLabels();
    if (this.preselectFirst) {
      this.preselect();
    }
  },
  methods: {
    calculateMenuWidthFromOptionLabels() {
      if (!Array.isArray(this.options) || !this.options.length) {
        return;
      }
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      const labels = options.map(option => {
        const isGroupOption = option.type === 'group';
        const label = isGroupOption ? get(option, this.groupLabelKey, '') : this.getOptionLabel(option).toString();
        return {
          type: option.type || 'option',
          label
        };
      });
      const minWidthFromLabels = calculateMinMenuWidthFromLabels(labels);
      this.computedMenuWidth = Math.round(clamp(minWidthFromLabels + this.extraLabelWidth, this.minMenuWidth, this.maxMenuWidth));
    },
    activate() {
      if (this.isOpen || this.isDisabled) {
        return;
      }
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      this.pointer = getNextWrappingIndex(1, -1, options, this.groupSelect, false);
      if (this.isSearchable) {
        const nonCustomTrigger = this.$el.querySelector('.pendo-multiselect__trigger');
        if (nonCustomTrigger) {
          const triggerBorderWidth = getBorderWidth(nonCustomTrigger) * 2;
          this.triggerWidth = nonCustomTrigger.getBoundingClientRect().width - triggerBorderWidth;
        }
      }
      this.isOpen = true;
      this.$emit('open', this.id);
    },
    deactivate() {
      if (!this.isOpen) {
        return;
      }
      let stopClose = false;
      const stop = () => {
        stopClose = true;
      };
      this.$emit('before-close', {
        stop
      });
      if (!stopClose) {
        this.$nextTick(() => {
          this.isOpen = false;
        });
        if (!this.preserveSearch) {
          setTimeout(() => {
            this.inputValue = '';
          }, 50);
        }
        this.$emit('close', this.selectedValue, this.id);
        if (this.$formItem) {
          this.$formItem.onFieldBlur(this.model);
        }
        setTimeout(() => {
          this.$emit('closed');
        }, 150);
      }
    },
    handleBlur() {
      this.isFocused = false;
    },
    handleFocus() {
      this.isFocused = true;
    },
    handleHide($event) {
      this.$emit('hide', $event, this.id);
      this.deactivate();
    },
    handleClickOutside: _async(function ($event) {
      const _this = this;
      let stopClose = false;
      const stop = () => {
        stopClose = true;
      };
      $event.stop = stop;
      _this.$emit('click-outside', $event, _this.id);
      return _invokeIgnored(function () {
        if (!stopClose) {
          return _await(_this.$nextTick(), function () {
            _this.deactivate();
          });
        }
      });
    }),
    handleKeydown(key, $event) {
      const hasInputValue = this.inputValue.length;
      const isFocusedOnSelectedValue = this.selectedValuePointer > -1;
      switch (key) {
        case 'left':
          if (!hasInputValue) {
            this.pointer = -1;
            this.focusPreviousSelectedValue();
          }
          break;
        case 'right':
          if (!hasInputValue) {
            this.pointer = -1;
            this.focusNextSelectedValue();
          }
          break;
        case 'up':
          this.resetSelectedValuePointer();
          this.pointerBackward();
          break;
        case 'down':
          this.resetSelectedValuePointer();
          this.pointerForward();
          break;
        case 'enter':
          if (isFocusedOnSelectedValue) {
            return;
          }
          if (!this.isOpen) {
            this.activate();
          } else {
            this.addPointerElement();
          }
          break;
        case 'esc':
          this.deactivate();
          break;
        case 'delete':
          {
            if (this.showSelectedValues) {
              const indexToRemove = isFocusedOnSelectedValue ? this.selectedValuePointer : this.model.length - 1;
              this.removeSelectionAtIndex(indexToRemove);
            }
            break;
          }
        case 'space':
          if (!this.isOpen) {
            this.activate();
            $event.preventDefault();
          } else if (!hasInputValue) {
            this.addPointerElement();
            $event.preventDefault();
          }
          break;
        case 'tab':
          if (this.isOpen) {
            if (this.pointer > 0) {
              this.addPointerElement('tab');
            }
            this.deactivate();
          }
          break;
      }
    },
    handleTriggerClick($event) {
      if ($event.detail === 0) {
        return;
      }
      this.toggleMenu();
    },
    isEntireGroupSelected(group) {
      return group[this.groupOptionsKey].every(option => this.isOptionSelected(option));
    },
    isNewOption(query) {
      if (isFunction(this.isValidNewOption)) {
        return this.isValidNewOption(query);
      }
      if (this.options) {
        return !this.optionKeys.includes(query);
      }
      return true;
    },
    isOptionSelected(option) {
      if (isObject(option)) {
        return this.model.some(opt => opt[this.valueKey] === option[this.valueKey]);
      }
      return this.model.includes(option);
    },
    getOptionLabel(option) {
      if (isEmpty(option)) {
        return '';
      }
      if (option.isTag) {
        return option.label;
      }
      const label = this.formatOptionLabel(option, this.labelKey);
      if (isEmpty(label)) {
        return '';
      }
      return label;
    },
    getOptionValue(option) {
      if (isObject(option)) {
        return option[this.valueKey];
      }
      return option;
    },
    preselect() {
      if (!this.model.length && this.options.length) {
        const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
        const optionIndex = getNextWrappingIndex(1, -1, options, this.groupSelect, false);
        if (options[optionIndex]) {
          this.select(options[optionIndex], null, true);
        }
      }
    },
    select(option, key) {
      let force = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      const isBlockedKeyEvent = this.blockKeys.includes(key);
      const isOptionDisabled = option && option.disabled;
      const isComponentDisabled = this.isDisabled;
      const shouldReturnEarly = isBlockedKeyEvent || isComponentDisabled || isOptionDisabled;
      if (shouldReturnEarly && !force) {
        return;
      }
      if (option && option.type === 'group') {
        if (!this.groupSelect) {
          return;
        }
        this.selectGroup(option);
      } else if (!Array.isArray(option)) {
        this.selectSingle(option);
      }
      if (this.closeOnSelect) {
        this.deactivate();
      }
    },
    selectGroup(selectedGroup) {
      const group = this.filteredOptions.find(option => {
        return option[this.groupLabelKey] === selectedGroup[this.groupLabelKey];
      });
      if (!group) {
        return;
      }
      if (this.isEntireGroupSelected(group)) {
        this.$emit('remove', group[this.groupOptionsKey], this.id);
        const newValue = this.model.filter(option => !group[this.groupOptionsKey].includes(option));
        this.$emit('input', newValue, this.id);
      } else {
        const optionsToAdd = group[this.groupOptionsKey].filter(not(this.isOptionSelected));
        this.$emit('select', optionsToAdd, this.id);
        this.$emit('input', this.model.concat(optionsToAdd), this.id);
      }
    },
    selectSingle(option, key) {
      const maxOptionsSelected = this.max && this.multiple && this.model.length === this.max;
      if (isNil(option) || maxOptionsSelected) {
        return;
      }
      if (option.isTag) {
        this.$emit('tag', option.label, this.id);
        if (this.closeOnSelect && !this.multiple) {
          this.deactivate();
        }
        this.inputValue = '';
      } else {
        if (this.isOptionSelected(option)) {
          if (key !== 'tab') {
            this.removeSelection(option);
          }
          return;
        }
        this.$emit('select', option, this.id);
        if (this.multiple) {
          this.$emit('input', this.model.concat([option]), this.id);
        } else {
          this.$emit('input', option, this.id);
        }
        if (this.clearOnSelect) {
          setTimeout(() => {
            this.inputValue = '';
          }, 50);
        }
      }
    },
    removeSelectionAtIndex(index) {
      if (this.blockKeys.indexOf('Delete') !== -1) {
        return;
      }
      if (this.multiple && this.inputValue.length === 0 && Array.isArray(this.model) && this.model.length) {
        this.removeSelection(this.model[index], false);
      }
    },
    removeSelection(option) {
      let shouldClose = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      if (this.isDisabled || option.disabled) {
        return;
      }
      if (!this.allowEmpty && this.model.length <= 1) {
        this.deactivate();
        return;
      }
      const indexToRemove = isObject(option) ? this.model.map(element => element[this.valueKey]).indexOf(option[this.valueKey]) : this.model.indexOf(option);
      this.$emit('remove', option, this.id);
      if (this.multiple) {
        const newValue = this.model.slice(0, indexToRemove).concat(this.model.slice(indexToRemove + 1));
        this.$emit('input', newValue, this.id);
        this.resetSelectedValuePointer();
      } else {
        this.$emit('input', null, this.id);
      }
      if (this.closeOnSelect && shouldClose) {
        this.deactivate();
      }
    },
    schedulePopperUpdate() {
      if (this.isOpen && this.$children[0] && this.$children[0].popperInstance) {
        this.$children[0].popperInstance.scheduleUpdate();
      }
    },
    toggleMenu() {
      if (this.isOpen) {
        this.deactivate();
      } else {
        this.activate();
      }
    },
    updateInputValue(value) {
      this.inputValue = value;
      this.resetSelectedValuePointer();
    },
    addPointerElement(withKey) {
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      if (options.length > 0) {
        let option = options[this.pointer];
        if (this.taggable && !option) {
          option = {
            top: options[0],
            bottom: options[options.length - 1]
          }[this.tagPosition];
        }
        this.select(option, withKey);
      }
      this.pointerReset();
    },
    pointerForward() {
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      this.shouldScrollToFocusedOption = true;
      if (this.pointer < options.length - 1) {
        this.pointer++;
        if (options[this.pointer] && options[this.pointer].type === 'group' && !this.groupSelect) {
          this.pointerForward();
        }
        if (options[this.pointer] && options[this.pointer].disabled) {
          this.pointerForward();
        }
      } else {
        this.pointer = getNextWrappingIndex(1, this.pointer, options, this.groupSelect);
      }
    },
    pointerBackward() {
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      this.shouldScrollToFocusedOption = true;
      if (this.pointer > 0) {
        this.pointer--;
        if (options[this.pointer] && options[this.pointer].type === 'group' && !this.groupSelect) {
          this.pointerBackward();
        }
        if (options[this.pointer] && options[this.pointer].disabled) {
          this.pointerBackward();
        }
      } else {
        this.pointer = getNextWrappingIndex(-1, this.pointer, options, this.groupSelect);
      }
    },
    pointerReset() {
      if (!this.closeOnSelect) {
        return;
      }
      this.pointer = 0;
    },
    pointerAdjust() {
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      if (this.pointer >= options.length - 1) {
        this.pointer = options.length ? options.length - 1 : 0;
      }
      if (options.length > 0 && options[this.pointer] && options[this.pointer].type === 'group' && !this.groupSelect) {
        this.pointerForward();
      }
    },
    pointerSet(index) {
      if (index === -1) {
        this.pointer = -1;
        return;
      }
      const options = this.hasGroups ? this.flattenedOptions : this.filteredOptions;
      if (options[index] && options[index].type === 'group' && !this.groupSelect) {
        return;
      }
      if (this.pointer === index) {
        return;
      }
      this.shouldScrollToFocusedOption = false;
      this.pointer = index;
    },
    resetSelectedValuePointer() {
      this.selectedValuePointer = -1;
    },
    focusNextSelectedValue() {
      const selectedCount = this.visibleSelectedValues.length;
      if (this.selectedValuePointer > -1 && this.selectedValuePointer < selectedCount - 1) {
        this.selectedValuePointer++;
      } else {
        this.resetSelectedValuePointer();
      }
    },
    focusPreviousSelectedValue() {
      const selectedCount = this.visibleSelectedValues.length;
      if (this.selectedValuePointer === -1) {
        this.selectedValuePointer = selectedCount - 1;
      } else if (this.selectedValuePointer > 0) {
        this.selectedValuePointer--;
      }
    }
  }
};

export { PendoMultiselectCore as default };
//# sourceMappingURL=pendo-multiselect-core.js.map
